import { useMemo, useState, useEffect } from "react";
import { DatePicker, Select } from "antd";
import useAxiosInstance from "../../../lib/useAxiosInstance";
import GenderPieChart from "./GmsStats/GenderPieChart";
import TopCategoriesBar from "./GmsStats/TopCategoriesBar";
import OfficeWiseChart from "./GmsStats/OfficeWiseChart";

const { RangePicker } = DatePicker;

const GrievanceStatsPanel = ({ grievances, selectedCategory, setSelectedCategory }) => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [duration, setDuration] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const axiosInstance = useAxiosInstance();

  // Handle range picker
  useEffect(() => {
    if (dateRange) {
      const [start, end] = dateRange;
      setStartDate(start.toISOString());
      setEndDate(end.toISOString());
    } else {
      setStartDate("");
      setEndDate("");
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, duration]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(
        `api/grievances/getGrievanceInsights?startDate=${startDate}&endDate=${endDate}&duration=${duration}`
      );
      setData(response?.data?.data);
    } catch (err) {
      console.error("Fetch Insights Error", err);
    } finally {
      setLoading(false);
    }
  };

  // Extract unique categories
  const categories = useMemo(() => {
    const unique = new Set();
    grievances.forEach((g) => {
      if (g?.properties?.cat_name) unique.add(g.properties.cat_name);
    });
    return Array.from(unique);
  }, [grievances]);

  // Add this derived value
const selectedCategoryCount = useMemo(() => {
  if (!selectedCategory) return 0;
  return grievances.filter(
    (g) => g?.properties?.cat_name === selectedCategory
  ).length;
}, [selectedCategory, grievances]);

  // Subcategories for selected category
  const subCategories = useMemo(() => {
    if (!selectedCategory) return [];
    const filtered = grievances.filter(
      (g) => g?.properties?.cat_name === selectedCategory
    );
    const subMap = {};
    filtered.forEach((g) => {
      const sub = g?.properties?.sub_cat_name || "Uncategorized";
      subMap[sub] = (subMap[sub] || 0) + 1;
    });
    return Object.entries(subMap).map(([subCat, count]) => ({
      subCat,
      count,
    }));
  }, [selectedCategory, grievances]);

  if (loading) {
    return (
      <div className="absolute top-16 right-4 bg-white/70 rounded-2xl shadow-lg w-[420px] h-[80vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="absolute top-16 right-4 bg-white/50 backdrop-blur-xl border border-white/40 rounded-3xl shadow-lg w-[420px] max-h-[90vh] overflow-y-auto z-[9] flex flex-col p-4 space-y-4">
      {/* Header */}
      <h3 className="text-lg font-bold text-[#004877]">Grievance Stats</h3>

      {/* Filters */}
      <div className="flex gap-3 justify-center">
        <RangePicker
          allowClear
          value={dateRange}
          onChange={(dates) => setDateRange(dates)}
          placeholder={["Start Date", "End Date"]}
          size="middle"
          className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2"
        />
        {/* <select
            value={duration}
            onChange={(e) => setDuration(e.target.value)}  // ✅ Fix: extract value from event
            className="w-full text-black shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                    relative overflow-hidden rounded-xl border backdrop-blur-xl
                    border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2"
            >
            <option value="" className="text-black/60">
                Select Duration
            </option>
            <option value="Past Week">Past Week</option>
            <option value="Past Month">Past Month</option>
            <option value="Past Quarter">Past Quarter</option>
            <option value="Past Year">Past Year</option>
        </select> */}
      </div>

      {/* Total Grievances */}
      {/* <div className="p-1 bg-gradient-to-r from-blue-50 to-emerald-50 rounded-2xl shadow">
        <h4 className="text-md font-bold text-gray-800">Total Grievances</h4>
        <p className="text-3xl font-extrabold text-[#004877]">
          {data.totalGrievance || 0}
        </p>
      </div> */}

      {/* Category Dropdown */}
      <div className="flex justify-between items-center flex-col">
         {selectedCategory && (
    <p className="text-sm txt-center font-medium text-[#004877]">
  <span className="flex items-center justify-center w-12 h-12 
                  text-2xl font-extrabold text-[#004877] 
                  border-2 border-black rounded-full">
    {selectedCategoryCount}
  </span>
</p>
  )}
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Filter by Category
        </label>
        <select
          className="w-full mb-3 text-black shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                    relative overflow-hidden rounded-xl border backdrop-blur-xl
                    border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2 px-2 py-1 outline-none"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option className="text-black/60" value="">
            All Categories
          </option>
          {categories.map((cat) => (
            <option key={cat} className="text-black" value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* If category selected → show subcategories */}
      {selectedCategory ? (
        <div className="p-3 bg-white/60 rounded-xl border">
          <h4 className="text-md font-bold text-[#004877] mb-2">
            Sub-categories in {selectedCategory}
          </h4>
          <ul className="space-y-1">
            {subCategories.map(({ subCat, count }) => (
              <li
                key={subCat}
                className="flex justify-between items-center text-sm text-gray-700"
              >
                <span>{subCat}</span>
                <span className="font-bold text-[#004877] border rounded-full bg-gray-200 px-2 py-1">
                  {count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <>
          {/* Charts (only when no category filter applied) */}
           <div className="flex justify-center gap-3 shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-2xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50">
              <div className="p-2">
                <h4 className="text-sm font-bold text-[#004877] mb-2">
                  Gender Distribution
                </h4>
                <div className="h-[7rem]">
                  <GenderPieChart data={data.genderWise || []} />
                </div>
              </div>
              <div className="p-2">
              <h4 className="text-sm font-bold text-[#004877] mb-2">
                Office-wise Distribution
              </h4>
              <div className="h-[7rem]">
                <OfficeWiseChart data={data.officeWise || []} />
              </div>
            </div>
          </div>
          <div className="shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-2xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50 p-2">
                <h4 className="text-md font-bold text-[#004877] mb-2">
                  Top 5 Categories
                </h4>
                <div className="h-[11rem]">
                  <TopCategoriesBar data={data.categoryWise || []} />
                </div>
          </div>   
        </>
      )}
    </div>
  );
};

export default GrievanceStatsPanel;
